import { InputGroup, Intent } from '@blueprintjs/core';
import isEqual from 'lodash.isequal';
import * as React from 'react';
import { Text } from 'vet-bones/components';

import {
  PROJECT_DIAGRAM_ENTITY_DRAWER_RELATIONSHIP_TYPE,
  getProjectDiagramEntityDrawerTableRowTestId,
} from 'src/constants/testIds';
import { ProjectDiagramClassDrawerRelationshipsDirections } from 'src/containers/ProjectDiagram/components/classDrawer/ProjectDiagramClassDrawerRelationshipsDirections';
import { ClassRelationshipItem } from 'src/containers/ProjectDiagram/components/classDrawer/types';
import {
  UnlinkButton,
  UpdateButton,
} from 'src/containers/ProjectDiagram/components/ProjectDiagramEntityDrawerButtons';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import * as copy from 'src/templates/copy';

type ProjectDiagramClassDrawerRelationshipsEditRowProps = {
  canUpdateDirectly?: boolean;
  idx: number;
  relationship: ClassRelationshipItem;
  onSubmit: (item: ClassRelationshipItem | null) => void;
  onUnlink: (item: ClassRelationshipItem) => void;
};

export const ProjectDiagramClassDrawerRelationshipsEditRow: React.VFC<
  ProjectDiagramClassDrawerRelationshipsEditRowProps
> = ({ canUpdateDirectly, idx, relationship, onSubmit, onUnlink }) => {
  const [localText, setLocalText] = React.useState(relationship.text);
  const [localDirections, setLocalDirections] = React.useState(
    relationship.directions
  );

  let tooltipContent = '';
  if (!localText) {
    tooltipContent =
      copy.components.projectDiagram.classDrawer.relationshipsTable.missingName;
  } else if (!localDirections.length) {
    tooltipContent =
      copy.components.projectDiagram.classDrawer.relationshipsTable
        .missingDirection;
  }
  const disabled = Boolean(tooltipContent);

  const handleUpdate = (text: string, directions: RelationshipDirection[]) => {
    if (disabled && !canUpdateDirectly) {
      return;
    }

    const needsUpdate =
      !isEqual(directions, relationship.directions) ||
      text !== relationship.text;

    onSubmit(
      needsUpdate
        ? {
            ...relationship,
            text,
            directions,
          }
        : null
    );
  };

  return (
    <tr
      className="sd-has-hidden-until-hover"
      data-testid={getProjectDiagramEntityDrawerTableRowTestId(
        relationship.value,
        PROJECT_DIAGRAM_ENTITY_DRAWER_RELATIONSHIP_TYPE,
        idx
      )}
    >
      <td>
        {/* currently, updating directly coincides with situations where we don't want the user editing the label */}
        {!canUpdateDirectly ? (
          <InputGroup
            intent={!localText ? Intent.DANGER : undefined}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              setLocalText(e.currentTarget.value);
            }}
            placeholder={relationship.text}
            value={localText}
          />
        ) : (
          <Text
            className="sd-drawer-project-diagram-entity-table-standin--input"
            ellipsize
          >
            {relationship.text}
          </Text>
        )}
      </td>
      <td>
        <ProjectDiagramClassDrawerRelationshipsDirections
          directions={localDirections}
          setDirections={(directions) => {
            setLocalDirections(directions);
            if (canUpdateDirectly) {
              handleUpdate(localText, directions);
            }
          }}
        />
      </td>
      <td>
        {canUpdateDirectly ? (
          <UnlinkButton onClick={() => onUnlink(relationship)} />
        ) : (
          <UpdateButton
            disabled={disabled}
            onClick={() => handleUpdate(localText, localDirections)}
            tooltip={tooltipContent}
          />
        )}
      </td>
    </tr>
  );
};
